import {
  ConnectorInstance,
  ConnectorInstanceElement,
  DatapointInstance,
  DefaultDatapointTypes,
  EventDatapointParameters,
  InlineDataPointInstance
} from '@olive/oli-types';
import { isConnectorInstance, isDatapointInstance, isInlineDatapointInstance } from '@olive/oli-types/src/typeutil/isOfType';
import * as EventEmitter from 'events';
import ConnectivityHolder, { ConnectivityDatapoint } from './ConnectivityHolder';
import AbstractDispatcher from './dispatchers/AbstractDispatcher';

export default class ConnectivityManager {
  holder: ConnectivityHolder;
  eventManager: EventEmitter;
  dispatchers: { [key: string]: AbstractDispatcher };

  constructor(props: {
    holder: ConnectivityHolder;
    eventManager: EventEmitter;
  }) {
    this.holder = props.holder;
    this.eventManager = props.eventManager;
    this.dispatchers = {};
    this.setupEventListeners();
  }

  setupEventListeners() {
    const connectivitys = this.holder.getAll();
    for (const key in connectivitys) {
      const datapoint = connectivitys[key];
      // we only care about connector instances here, not about connector definitions because
      // client side events can only exist in connector instances
      if (isConnectorInstance(datapoint)) {
        if (datapoint.elements) {
          //TODO FBA: connector reference does not work as expected in for the connector
          const entries = datapoint.elements;
          for (let i = 0; i < entries.length; i++) {
            const resolvedConnectivity = this.resolveItem(entries[i]);
            if (!resolvedConnectivity || !('type' in resolvedConnectivity)) {
              continue;
            }
            if (resolvedConnectivity.type === DefaultDatapointTypes.EVENT) {
              const componentInstanceID = (entries[i] as DatapointInstance).parameters?.componentInstanceID;

              const eventTopic = (
                (resolvedConnectivity as DatapointInstance).datapoint as EventDatapointParameters
              ).event.topic;
              const eventID = componentInstanceID ? componentInstanceID + '-' + eventTopic : eventTopic;
              console.info('Listening on event: ', eventID);
              this.eventManager.on(
                eventID,
                this.onEvent.bind(this, datapoint.instanceID || datapoint.instanceOf)
              );
            }
          }
        }
        const connectorInstance = datapoint;
        const source = connectorInstance.elements[0];
        if (!source) {
          continue;
        }
        //@ts-ignore TODO MHA source should be possible to be of type ConnectorTask
        const sourceDatapointID = typeof source === 'string' ? source : source.instanceID || source.id || source.datapoint || source.instanceOf;
        const sourceDatapoint = connectivitys[sourceDatapointID] as ConnectivityDatapoint;
        if (!sourceDatapoint) {
          //TODO FBA: Discuss this with olive team, we know make an event after every api call and people can register onto it
          const strippedText: string = sourceDatapointID.replace(
            /^onAfter_(.*)/,
            '$1'
          );
          const sourceDatapoint = connectivitys[strippedText] as ConnectivityDatapoint;
          if (!sourceDatapoint) {
            continue;
          }
          this.eventManager.on(
            sourceDatapointID,
            this.onEvent.bind(this, connectorInstance.instanceID || connectorInstance.instanceOf)
          );

          continue;
        }
        if (sourceDatapoint?.datapoint?.type === DefaultDatapointTypes.EVENT) {
          const componentInstanceID = isDatapointInstance(source) ? (source as DatapointInstance).parameters?.componentInstanceID : null;
          const eventTopic = (
            sourceDatapoint.datapoint as EventDatapointParameters
          ).event.topic;
          const eventID = componentInstanceID ? componentInstanceID + '-' + eventTopic : eventTopic;
          console.info('Listening on event: ', eventID);
          this.eventManager.on(
            eventID,
            this.onEvent.bind(this, connectorInstance.instanceID || connectorInstance.instanceOf)
          );
        }
      }
    }
  }

  private resolveItem(item: string | DatapointInstance | InlineDataPointInstance | ConnectorInstance | ConnectorInstanceElement) {
    let id: string;
    let resolvedConnectivity: DatapointInstance | ConnectorInstance | InlineDataPointInstance;
    if (typeof (item) === 'string') {
      id = item;
    }

    else {
      const isInlineDatapointInstanceResult = isInlineDatapointInstance(item);
      const isDatapointInstanceResult = isDatapointInstance(item);
      const isConnectorInstanceResult = isConnectorInstance(item);
      if (isInlineDatapointInstanceResult) {
        resolvedConnectivity = item;
      }
      else if (isDatapointInstanceResult) {
        const datapoint = (item as DatapointInstance).datapoint;
        if (typeof (datapoint) === 'string') {
          id = datapoint;
        }

        else {
          resolvedConnectivity = datapoint;
        }
      }
      else if (isConnectorInstanceResult) {
        // events can only be first item se we disregard the others
        const datapoint = (item as ConnectorInstance).elements[0];
        if (typeof (datapoint) === 'string') {
          id = datapoint;
        }

        else {
          resolvedConnectivity = datapoint as ConnectorInstance;
        }
      }

      else {
        console.error('Unknown item type: ', item);
      }
    }
    if (!resolvedConnectivity) {
      resolvedConnectivity = this.holder.get(id);
    }
    return resolvedConnectivity;
  }

  async onEvent(
    connectorID: string,
    params?: unknown
  ) {
    let input: any = {};
    if (params instanceof FormData) {
      input.formData = params;
    } else if (typeof params === 'object' && params !== null) {
      input = { ...params };
    }
    const connector = this.holder.get(connectorID) as ConnectorInstance;
    const connectorInput = connector.parameters || {};

    input = { ...input, ...connectorInput };

    const result = await this.invoke({
      id: connectorID,
      input,
    });
    return result;
  }

  async invoke(props: { id: string; input?: unknown; parameters?: any }) {
    for (const key in this.dispatchers) {
      const dispatcher = this.dispatchers[key];
      if (dispatcher.canDispatch({ id: props.id })) {
        return await dispatcher.dispatch(props);
      }
    }
    console.error('No dispatcher found that can dispatch ' + props.id);
    throw 'No dispatcher found that can dispatch ' + props.id;
  }

  registerDispatcher(id: string, dispatcher: AbstractDispatcher) {
    if (this.dispatchers[id]) {
      console.warn('Dispatcher with id ' + id + ' already registered');
    }
    this.dispatchers[id] = dispatcher;
  }
}
